import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DataTable, type Column } from '@/components/ui/data-table';
import {
  Search,
  RefreshCw,
  Eye,
  X,
  AlertTriangle,
  LayoutList,
  Table2,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import {
  getAuditEntries,
  type AuditEntryListItem,
  type AuditEntriesParams,
} from '@/api/v2-activity';
import {
  ACTION_LABELS,
  ENTITY_TYPE_LABELS,
  actionLabel,
  actionBadgeClass,
  entityTypeLabel,
  formatDateTime,
  formatRelativeDate,
} from './labels';
import { AuditEntrySheet } from './AuditEntrySheet';
import { ActivityFeed } from './ActivityFeed';
import { cn } from '@/lib/utils';

const PERIOD_OPTIONS = [
  { value: 'all', label: 'Toute la période' },
  { value: '24h', label: 'Dernières 24 h' },
  { value: '7d', label: '7 derniers jours' },
  { value: '30d', label: '30 derniers jours' },
  { value: 'custom', label: 'Personnalisée' },
] as const;

const PERIOD_MS: Record<string, number> = {
  '24h': 24 * 3600 * 1000,
  '7d': 7 * 24 * 3600 * 1000,
  '30d': 30 * 24 * 3600 * 1000,
};

const LIVE_REFRESH_INTERVAL_MS = 30_000;

interface ActivityFilters {
  page: number;
  limit: number;
  search: string;
  action: string;
  entityType: string;
  entityId: string;
  userId: string;
  userLabel: string;
  period: string;
  from: string;
  to: string;
}

/**
 * Suivi d'activité V2 d'une école : tableau paginé server-side du journal
 * d'audit (qui a fait quoi, quand), avec filtres période/action/type/recherche
 * et drill-down par entrée. Les filtres vivent dans l'URL (préfixe `act_`)
 * pour permettre le partage de liens filtrés entre support et développeurs.
 */
export function V2ActivityTab({ schoolCode }: { schoolCode: string }) {
  const [searchParams, setSearchParams] = useSearchParams();

  const filters: ActivityFilters = useMemo(
    () => ({
      page: Number(searchParams.get('act_page')) || 1,
      limit: Number(searchParams.get('act_limit')) || 25,
      search: searchParams.get('act_search') || '',
      action: searchParams.get('act_action') || 'all',
      entityType: searchParams.get('act_type') || 'all',
      entityId: searchParams.get('act_entity') || '',
      userId: searchParams.get('act_user') || '',
      userLabel: searchParams.get('act_user_label') || '',
      period: searchParams.get('act_period') || '7d',
      from: searchParams.get('act_from') || '',
      to: searchParams.get('act_to') || '',
    }),
    [searchParams]
  );

  const updateFilters = useCallback(
    (updates: Partial<ActivityFilters>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          const merged = { ...filters, ...updates };
          const defaults: ActivityFilters = {
            page: 1,
            limit: 25,
            search: '',
            action: 'all',
            entityType: 'all',
            entityId: '',
            userId: '',
            userLabel: '',
            period: '7d',
            from: '',
            to: '',
          };
          const keys: Record<keyof ActivityFilters, string> = {
            page: 'act_page',
            limit: 'act_limit',
            search: 'act_search',
            action: 'act_action',
            entityType: 'act_type',
            entityId: 'act_entity',
            userId: 'act_user',
            userLabel: 'act_user_label',
            period: 'act_period',
            from: 'act_from',
            to: 'act_to',
          };
          (Object.keys(keys) as (keyof ActivityFilters)[]).forEach((key) => {
            const value = merged[key];
            if (value === defaults[key]) {
              next.delete(keys[key]);
            } else {
              next.set(keys[key], String(value));
            }
          });
          return next;
        },
        { replace: true }
      );
    },
    [filters, setSearchParams]
  );

  // Vue : « feed » = fil narratif (première lecture, support/direction),
  // « table » = tableau technique (seconde lecture, développeurs).
  const view = searchParams.get('act_view') === 'table' ? 'table' : 'feed';
  const setView = useCallback(
    (next: 'feed' | 'table') => {
      setSearchParams(
        (prev) => {
          const nextParams = new URLSearchParams(prev);
          if (next === 'feed') {
            nextParams.delete('act_view');
          } else {
            nextParams.set('act_view', next);
          }
          return nextParams;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const [items, setItems] = useState<AuditEntryListItem[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [enriching, setEnriching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [noDeployment, setNoDeployment] = useState(false);
  const [live, setLive] = useState(false);
  const [selectedEntryId, setSelectedEntryId] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState(filters.search);

  // Garde anti-course : seule la dernière requête lancée a le droit d'écrire
  // l'état (les fetches en deux phases peuvent se chevaucher entre filtres).
  const fetchSeqRef = useRef(0);

  const fetchEntries = useCallback(
    async (silent = false) => {
      const seq = ++fetchSeqRef.current;
      if (!silent) setLoading(true);
      try {
        setError(null);
        const params: AuditEntriesParams = {
          page: filters.page,
          limit: filters.limit,
        };
        if (filters.search) params.search = filters.search;
        if (filters.action !== 'all') params.action = filters.action;
        if (filters.entityType !== 'all') params.entityType = filters.entityType;
        if (filters.entityId) params.entityId = filters.entityId;
        if (filters.userId) params.userId = filters.userId;

        if (filters.period === 'custom') {
          if (filters.from) params.from = new Date(filters.from).toISOString();
          if (filters.to) params.to = new Date(`${filters.to}T23:59:59`).toISOString();
        } else if (filters.period !== 'all') {
          params.from = new Date(Date.now() - PERIOD_MS[filters.period]).toISOString();
        }

        // Phase 1 — liste brute, affichée immédiatement (les noms et valeurs
        // arrivent en phase 2). En rafraîchissement silencieux (Live), on va
        // directement à la version enrichie.
        if (!silent) {
          const raw = await getAuditEntries(schoolCode, { ...params, enrich: 0 });
          if (seq !== fetchSeqRef.current) return;
          setItems(raw.items ?? []);
          setTotalItems(raw.total ?? 0);
          setTotalPages(raw.pages ?? 1);
          setNoDeployment(false);
          setLoading(false);
          setEnriching(true);
        }

        // Phase 2 — version enrichie (noms des objets + diffs), servie par le
        // cache ADM dès la seconde visite.
        const data = await getAuditEntries(schoolCode, params);
        if (seq !== fetchSeqRef.current) return;
        setItems(data.items ?? []);
        setTotalItems(data.total ?? 0);
        setTotalPages(data.pages ?? 1);
        setNoDeployment(false);
      } catch (err) {
        if (seq !== fetchSeqRef.current) return;
        const status = (err as { response?: { status?: number } })?.response?.status;
        if (status === 404) {
          setNoDeployment(true);
        } else {
          setError("Erreur lors du chargement du journal d'activité");
        }
      } finally {
        if (seq === fetchSeqRef.current) {
          setLoading(false);
          setEnriching(false);
        }
      }
    },
    [schoolCode, filters]
  );

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  // Recherche debouncée → filtre `search` (plein-texte V2 sur type + action)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== filters.search) {
        updateFilters({ search: searchInput, page: 1 });
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]); // eslint-disable-line react-hooks/exhaustive-deps

  // Mode live : rafraîchissement silencieux périodique
  const fetchRef = useRef(fetchEntries);
  fetchRef.current = fetchEntries;
  useEffect(() => {
    if (!live) return;
    const interval = setInterval(() => fetchRef.current(true), LIVE_REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [live]);

  const entityTypeOptions = useMemo(
    () =>
      Object.entries(ENTITY_TYPE_LABELS)
        .map(([value, label]) => ({ value, label }))
        .sort((a, b) => a.label.localeCompare(b.label, 'fr')),
    []
  );

  const columns: Column<AuditEntryListItem>[] = [
    {
      key: 'occurredAt',
      header: 'Date',
      className: 'whitespace-nowrap',
      render: (item) => (
        <div>
          <div className="font-medium">{formatRelativeDate(item.occurredAt)}</div>
          <div className="text-xs text-gray-500 dark:text-slate-400">
            {formatDateTime(item.occurredAt)}
          </div>
        </div>
      ),
    },
    {
      key: 'action',
      header: 'Action',
      render: (item) => (
        <Badge className={cn('border-transparent', actionBadgeClass(item.action))}>
          {actionLabel(item.action)}
        </Badge>
      ),
    },
    {
      key: 'entityType',
      header: 'Objet',
      render: (item) => (
        <div>
          <div className="font-medium">
            {item.entityLabel ?? entityTypeLabel(item.entityType)}
          </div>
          <div className="text-xs text-gray-400">
            {item.entityLabel && <span>{entityTypeLabel(item.entityType)} · </span>}
            <span className="font-mono">{item.entityId.slice(0, 8)}…</span>
          </div>
        </div>
      ),
    },
    {
      key: 'actor',
      header: 'Utilisateur',
      render: (item) => (
        <span className={cn(!item.actorLabel && 'text-gray-500 italic')}>
          {item.actorLabel ?? 'Système'}
        </span>
      ),
    },
    {
      key: 'changedFieldsCount',
      header: 'Modifications',
      className: 'text-center',
      render: (item) =>
        item.changedFieldsCount > 0 ? (
          <span>
            {item.changedFieldsCount} champ{item.changedFieldsCount > 1 ? 's' : ''}
          </span>
        ) : (
          <span className="text-gray-400">—</span>
        ),
    },
    {
      key: 'details',
      header: '',
      className: 'text-right w-12',
      render: (item) => (
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={() => setSelectedEntryId(item.id)}
          title="Voir le détail"
        >
          <Eye className="h-4 w-4" />
        </Button>
      ),
    },
  ];

  if (noDeployment) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-gray-500 dark:text-slate-400">
          <AlertTriangle className="h-8 w-8 mx-auto mb-3 text-amber-500" />
          Cette école n'a pas de déploiement V2 : aucun journal d'activité disponible.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Barre de filtres */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative w-64">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <Input
            type="text"
            placeholder="Rechercher (type, action)…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="pl-9"
          />
        </div>

        <Select
          value={filters.period}
          onValueChange={(value) => updateFilters({ period: value, page: 1 })}
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PERIOD_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {filters.period === 'custom' && (
          <div className="flex items-center gap-2">
            <Input
              type="date"
              value={filters.from}
              onChange={(e) => updateFilters({ from: e.target.value, page: 1 })}
              className="w-[150px]"
            />
            <span className="text-sm text-gray-500">→</span>
            <Input
              type="date"
              value={filters.to}
              onChange={(e) => updateFilters({ to: e.target.value, page: 1 })}
              className="w-[150px]"
            />
          </div>
        )}

        <Select
          value={filters.action}
          onValueChange={(value) => updateFilters({ action: value, page: 1 })}
        >
          <SelectTrigger className="w-[190px]">
            <SelectValue placeholder="Action" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes les actions</SelectItem>
            {Object.entries(ACTION_LABELS).map(([value, label]) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={filters.entityType}
          onValueChange={(value) => updateFilters({ entityType: value, entityId: '', page: 1 })}
        >
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Type d'objet" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les objets</SelectItem>
            {entityTypeOptions.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="ml-auto flex items-center gap-3">
          <div className="flex rounded-lg border border-gray-200 dark:border-slate-700 overflow-hidden">
            <button
              type="button"
              onClick={() => setView('feed')}
              title="Fil d'activité (lecture simple)"
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 text-sm transition-colors',
                view === 'feed'
                  ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 font-medium'
                  : 'text-gray-500 hover:bg-gray-50 dark:hover:bg-slate-800'
              )}
            >
              <LayoutList className="h-4 w-4" />
              Fil
            </button>
            <button
              type="button"
              onClick={() => setView('table')}
              title="Tableau technique (développeurs)"
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 text-sm border-l border-gray-200 dark:border-slate-700 transition-colors',
                view === 'table'
                  ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 font-medium'
                  : 'text-gray-500 hover:bg-gray-50 dark:hover:bg-slate-800'
              )}
            >
              <Table2 className="h-4 w-4" />
              Tableau
            </button>
          </div>
          <div className="flex items-center gap-2">
            <Switch id="activity-live" checked={live} onCheckedChange={setLive} />
            <Label htmlFor="activity-live" className="text-sm cursor-pointer">
              Live
            </Label>
          </div>
          <Button variant="outline" size="sm" onClick={() => fetchEntries()} disabled={loading}>
            <RefreshCw
              className={cn('h-4 w-4 mr-1.5', (loading || enriching) && 'animate-spin')}
            />
            Actualiser
          </Button>
          {enriching && (
            <span className="text-xs text-gray-400 dark:text-slate-500 animate-pulse">
              Chargement des noms…
            </span>
          )}
        </div>
      </div>

      {/* Chips des filtres de rebond (utilisateur / objet précis) */}
      {(filters.userId || filters.entityId) && (
        <div className="flex flex-wrap items-center gap-2">
          {filters.userId && (
            <Badge variant="secondary" className="gap-1 pr-1">
              Utilisateur : {filters.userLabel || `${filters.userId.slice(0, 8)}…`}
              <button
                type="button"
                onClick={() => updateFilters({ userId: '', userLabel: '', page: 1 })}
                className="rounded-full hover:bg-gray-200 dark:hover:bg-slate-700 p-0.5"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}
          {filters.entityId && (
            <Badge variant="secondary" className="gap-1 pr-1">
              Historique : {entityTypeLabel(filters.entityType)} {filters.entityId.slice(0, 8)}…
              <button
                type="button"
                onClick={() => updateFilters({ entityId: '', entityType: 'all', page: 1 })}
                className="rounded-full hover:bg-gray-200 dark:hover:bg-slate-700 p-0.5"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}
        </div>
      )}

      {error && (
        <Card>
          <CardContent className="py-4 text-sm text-red-600 dark:text-red-400 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            {error}
          </CardContent>
        </Card>
      )}

      {view === 'feed' ? (
        <>
          <ActivityFeed items={items} loading={loading} onOpenEntry={setSelectedEntryId} />
          {totalPages > 1 && (
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500 dark:text-slate-400">
                {totalItems} événement{totalItems > 1 ? 's' : ''}
              </span>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => updateFilters({ page: filters.page - 1 })}
                  disabled={filters.page <= 1 || loading}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Plus récent
                </Button>
                <span className="px-3 text-sm text-gray-500 dark:text-slate-400">
                  Page {filters.page} sur {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => updateFilters({ page: filters.page + 1 })}
                  disabled={filters.page >= totalPages || loading}
                >
                  Plus ancien
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </>
      ) : (
        <DataTable<AuditEntryListItem>
          columns={columns}
          data={items}
          loading={loading}
          page={filters.page}
          pageSize={filters.limit}
          totalItems={totalItems}
          totalPages={totalPages}
          onPageChange={(page) => updateFilters({ page })}
          onPageSizeChange={(limit) => updateFilters({ limit, page: 1 })}
          pageSizeOptions={[25, 50, 100]}
          keyExtractor={(item) => item.id}
          emptyMessage="Aucune activité sur la période sélectionnée"
        />
      )}

      <AuditEntrySheet
        schoolCode={schoolCode}
        entryId={selectedEntryId}
        entityLabel={items.find((i) => i.id === selectedEntryId)?.entityLabel}
        onClose={() => setSelectedEntryId(null)}
        onFilterUser={(userId) => {
          const entry = items.find((i) => i.actorId === userId);
          updateFilters({ userId, userLabel: entry?.actorLabel ?? '', page: 1 });
          setSelectedEntryId(null);
        }}
        onFilterEntity={(entityType, entityId) => {
          updateFilters({ entityType, entityId, page: 1 });
          setSelectedEntryId(null);
        }}
      />
    </div>
  );
}
